import { defineComponent, computed } from "vue";
import { UserManager } from "oidc-client";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import { useApp } from "../../useApp";

import config from "../../../../common/config";
import AuthService from "../../../../services/auth.service";
import { PermissionManager } from "../../../../common/utilities/permission-manager";
import { useLoadingIndicator } from "../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { ApplicationEnum, ParentPermissionEnum, PermissionActionEnum } from "../../../../enums/enums";
import { ToastrService } from "../../../../services/toastr.service";
import { helperTestingAgency } from "../../../../common/vue-helpers/testing-agency-check";
import { IdentityService } from "../../../../services/identity-service";
import { IGetuserDetailResponse, UserService } from "../../../../services/user-service";
import { BranchesService } from "../../../../services/branches-service";

import MessagesPreview from "../../../../resources-vue/vue-custom-elements/MessagingService/MessagesPreview/MessagesPreview.vue";
import GetLiveLoginToken from "../../../../resources-vue/vue-dialogs/GetLiveLoginToken/GetLiveLoginToken.vue";

export default defineComponent({
    props: {
        areRolesLoaded: { type: Boolean, default: false },
        showEmployees: {
            type: Boolean,
            default: false
        },
        showPatients: {
            type: Boolean,
            default: false
        }
    },

    components: {
        MessagesPreview
    },

    data() {
        return {
            _userManager: null,
            _authService: null,
            _userService: null,
            _agencyService: null,
            _user: null,
            isLoggedIn: false,
            userFirstName: null,
            _permissionManager: null,
            _toastrService: null,
            displayRoutes: [],
            isSearchExpanded: false,
            showEmployeeSearch: false,
            organizationId: null,
            organizationName: null,
            dialogRef: null,
            isAxxessPlannerHeader: true,
            _identityService: null,
            liveLoginData: null,
            showExternalReferral: false,
            originUrl: window.location.origin,
            originName: "Axxess Central",
            userbetaInfo: null as IGetuserDetailResponse,
            _branchService: null,
            emailsToggle: [
                "opote@axxess.com",
                "axxesscareacc@gmail.com",
                "skamareddi@axxess.com",
                "tpn3k@msn.com",
                "tpn3k@yahoo.com",
                "ssobush@axxess.com",
                "oobielodan@axxess.com",
                "rreddy@axxess.com",
                "pajayi@axxess.com",
                "oaderibigbe@axxess.com",
                "undukwe@axxess.com",
                "npalandurkar@axxess.com",
                "oayeni@axxess.com",
                "JKlein@axxess.com",
                "ptale@axxess.com",
                "securitykeymethod@yopmail.com"
            ] as string[]
        };
    },
    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },
    async created() {
        this._userManager = Container.instance.get(UserManager);
        this._authService = Container.instance.get(AuthService);
        this._userService = Container.instance.get(UserService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._toastrService = Container.instance.get(ToastrService);
        this._identityService = Container.instance.get(IdentityService);
        this._branchService = Container.instance.get(BranchesService);

        this.$watch(
            () => this.$route.params,
            () => {
                // react to route changes...
                this.organizationName = window.localStorage.getItem(config.organizationName);
                this.organizationId = window.localStorage.getItem(config.organizationId);
            }
        );

        this.organizationName = window.localStorage.getItem(config.organizationName);
        this.organizationId = window.localStorage.getItem(config.organizationId);
    },
    watch: {
        areRolesLoaded() {
            this.areRolesLoadedChanged();
        },
        async organizationId() {
            this._branchService.emptyBranches();
            helperTestingAgency.resetTestingAgencyCheck();
            this.showExternalReferral = false; // hide external referrals pending when testing agency check is done
            this.showExternalReferral =
                (await helperTestingAgency.checkHasTestingAgency()) ||
                (await helperTestingAgency.checkHasExternalReferralTestingAgencies());
        }
    },
    emits: ["update:showEmployees", "update:showPatients"],
    async mounted() {
        this.loadUser();
        this.userbetaInfo = await this._userService.getUserDetails();
        this.areRolesLoadedChanged();
        const hasTestAgency =
            (await helperTestingAgency.checkHasTestingAgency()) ||
            (await helperTestingAgency.checkHasExternalReferralTestingAgencies());
        this.showExternalReferral = hasTestAgency;
        const employeeInfoViewAcess =
            this._permissionManager.getPermmission(
                ParentPermissionEnum.employeeInformation,
                PermissionActionEnum.canView
            ) || [];

        this.showEmployeeSearch = employeeInfoViewAcess?.length;
        this.liveLoginData = await this._identityService.getLiveLoginToken();
        this.originUrl = this.originUrl.concat(this.$route.path);
    },

    computed: {
        ...mapState(useApp, [
            "organizations",
            "showFrequencyWatch",
            "showHosNav",
            "hasAxxessConnectPermission",
            "isAnyPremiumAgency",
            "isClinician"
        ]),
        patientsTooltip() {
            return `Click to ${this.showPatients ? "hide" : "view"} your patients.`;
        },
        employeesTooltip() {
            return `Click to ${this.showEmployees ? "hide" : "view"} your employees.`;
        },
        showRoute() {
            return (routeName: string) => {
                if (routeName === "tabExternalReferral") {
                    if (this.showExternalReferral) {
                        return true;
                    } else {
                        return false;
                    }
                } else if (routeName === "tabFrequencyWatch") {
                    if (this.showFrequencyWatch) {
                        return true;
                    } else {
                        return false;
                    }
                } else if (routeName === "tabUnassignedPatientVisit") {
                    return this.isShowIfNotOnlyHH();
                } else if (routeName === "tabHUVandSFVStatusReport") {
                    if (this.showHosNav) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return true;
                }
            };
        },
        getProfileUrl() {
            return localStorage.getItem("profileUrl");
        },
        isConnectEnabled() {
            return this.hasAxxessConnectPermission && this.isAnyPremiumAgency;
        }
    },
    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        redirectToProfile() {
            window.location.href = this.getProfileUrl;
        },

        navigateToAxxessCentral() {
            this.$router.push(!this.isClinician ? "/help" : "/clinician");
        },

        navigateToAxxessChat() {
            this.$router.push("/central-messaging/clinicians/conversation");
        },

        async loadUser() {
            this._user = await this._userManager.getUser();
            if (this._user) {
                // this.isLoggedIn = await this._authService.isLoggedIn();
                this.userFirstName = this._user.profile.given_name;
            }
        },
        logout(): void {
            this._authService.logout();
        },

        areRolesLoadedChanged() {
            const isScheduler = this._permissionManager.checkPermission(
                ParentPermissionEnum.scheduler,
                PermissionActionEnum.canView
            );
            const isAdmin = this._permissionManager.checkPermission(
                ParentPermissionEnum.CompanySetup,
                PermissionActionEnum.canView
            );
            const isAddHospiceInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            );
            const isViewHospiceInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canView,
                ApplicationEnum.AxxessHospiceFE
            );

            const isEditHospiceInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canEdit,
                ApplicationEnum.AxxessHospiceFE
            );

            const isAddHospiceReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessHospiceFE
            );
            const isViewHospiceReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canView,
                ApplicationEnum.AxxessHospiceFE
            );

            const isEditHospiceReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canEdit,
                ApplicationEnum.AxxessHospiceFE
            );

            const isAddPalliativeInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            );
            const isViewPalliativeInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canView,
                ApplicationEnum.AxxessPalliative
            );

            const isEditPalliativeInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canEdit,
                ApplicationEnum.AxxessPalliative
            );

            const isAddPalliativeReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canAdd,
                ApplicationEnum.AxxessPalliative
            );
            const isViewPalliativeReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canView,
                ApplicationEnum.AxxessPalliative
            );

            const isEditPalliativeReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canEdit,
                ApplicationEnum.AxxessPalliative
            );

            const isAddHomeCareInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canAdd,
                ApplicationEnum.HomeCare
            );
            const isViewHomeCareInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canView,
                ApplicationEnum.HomeCare
            );

            const isEditHomeCareInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canEdit,
                ApplicationEnum.HomeCare
            );

            const isViewHHInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canView,
                ApplicationEnum.AgencyCore
            );

            const isEditHHInquiry = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.inquiries,
                PermissionActionEnum.canEdit,
                ApplicationEnum.AgencyCore
            );
            const isViewHHReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canView,
                ApplicationEnum.AgencyCore
            );

            const isEditHHReferral = this._permissionManager.checkPermissionByProduct(
                ParentPermissionEnum.referrals,
                PermissionActionEnum.canEdit,
                ApplicationEnum.AgencyCore
            );

            const showCentralIntake =
                isAddHospiceInquiry ||
                isEditHospiceInquiry ||
                isViewHospiceInquiry ||
                isAddHospiceReferral ||
                isEditHospiceReferral ||
                isViewHospiceReferral ||
                isAddPalliativeInquiry ||
                isEditPalliativeInquiry ||
                isViewPalliativeInquiry ||
                isAddPalliativeReferral ||
                isEditPalliativeReferral ||
                isViewPalliativeReferral ||
                isAddHomeCareInquiry ||
                isViewHomeCareInquiry ||
                isEditHomeCareInquiry ||
                isViewHHInquiry ||
                isEditHHInquiry ||
                isViewHHReferral ||
                isEditHHReferral;

            const routes = this.getAllActualRoutes();
            // Filtering routes to display tabs based on roles
            this.displayRoutes = routes?.filter((route: any) => {
                if (!!route.meta?.showIfScheduler) {
                    return isScheduler;
                } else if (!!route.meta?.showCentralIntake) {
                    return showCentralIntake;
                } else if (!!route.meta?.showIfAdmin) {
                    // Email Toggle logic
                    return isAdmin && this.emailsToggle.includes(this.userbetaInfo?.Data.EmailAddress); // email-Toggle
                } else {
                    return true;
                }
            });
        },
        getAllActualRoutes() {
            return this.$router.options.routes?.filter((route: any) => route.meta?.centralNav) || [];
        },

        isActivePath(path: string) {
            return this.$route.matched?.some((route) => route.path == path);
        },
        closeNotifications() {
            this._toastrService.clear();
        },
        goToRoute(path: string) {
            this.$router.push(path);
        },
        toggleEmployeesMenus() {
            this.$emit("update:showEmployees", !this.showEmployees);
            if (this.showPatients) {
                this.$emit("update:showPatients", false);
            }
        },

        togglePatientsMenu(): void {
            this.$emit("update:showPatients", !this.showPatients);
            if (this.showEmployees) {
                this.$emit("update:showEmployees", false);
            }
        },

        changeOrganization() {
            let name = this.$route.name.toString();
            this.$router.push(`/axxess-central/organizationLoading/?&name=${name}&fromPlanner=false`);
        },

        async displayLiveLoginToken() {
            let response = this.liveLoginData;
            this.dialogRef = this.$dialog.open(GetLiveLoginToken, {
                props: {
                    modal: true,
                    showHeader: false
                },
                data: {
                    response
                }
            });
        },

        isShowIfNotOnlyHH() {
            const organizationId = window.localStorage.getItem(config.organizationId);
            const organization = this.organizations?.find((org) => org.id === organizationId);
            const isOnlyHH = organization?.agencies.every(
                (agency) => agency.application === ApplicationEnum.AgencyCore
            );
            return isOnlyHH ? false : true;
        }
    }
});
